<template>
  <f7-list
    v-for="(item) in articleComponents"
    :key="item.key"
    class="parts-details-card-articles"
    accordion-list
    accordion-opposite
  >
    <f7-list-item
      :title="item.title"
      accordion-item
      :accordion-item-opened="accordionItemStatus[item.key]"
      @accordion:open="accordionItemStatus[item.key] = true"
      @accordion:close="accordionItemStatus[item.key] = false"
    >
      <template
        v-if="item.key === 'real' && !isPartFinished"
        #after
      >
        <f7-button
          :title="$t('common.warehouses')"
          @click="openWarehousesPopup"
        >
          <UCSvgIcon
            name="ic_batch_2_orange"
            width="32px"
            height="32px"
          />
        </f7-button>

        <f7-button
          :title="$t('common.articles')"
          @click="isAddArticlePopupOpened = true"
        >
          <img
            src="@/static/img/ic_add_orange.svg"
            width="32px"
            height="32px"
          >
        </f7-button>
      </template>

      <f7-accordion-content>
        <component :is="item.component" />
      </f7-accordion-content>
    </f7-list-item>
  </f7-list>

  <!-- Popup: Part Warehouses -->
  <PCPartWarehousesPopup
    ref="warehousesPopup"
    @added="handleWarehouseArticleAdded"
  />

  <!-- Popup: Add Article -->
  <PCPartAddArticlePopup
    v-model:opened="isAddArticlePopupOpened"
    :work-part-id="workPartDetails?.Id_ParteProduccion"
    @success="onAddArticleSuccess"
  />
</template>

<script setup>
// @ts-check
import {
  computed,
  onMounted,
  ref,
  shallowRef,
} from 'vue';
import { useRepairParts } from '@/composables/useRepairParts';
import { useParamRef } from '@erpagroapp/params';

import UCSvgIcon from '@/components/common/UCSvgIcon.vue';
import PCPartsDetailsCardArticlesExpected from './PCPartsDetailsCardArticlesExpected.vue';
import PCPartsDetailsCardArticlesReal from './PCPartsDetailsCardArticlesReal.vue';
import PCPartWarehousesPopup from '../PCPartWarehousesPopup.vue';
import PCPartAddArticlePopup from '../PCPartAddArticlePopup.vue';

/** @import { Ref, ShallowRef } from 'vue' */
/** @import { WorkPartItem } from '@/js/stores/repairParts' */

/** @type {Ref<Record<string, boolean>>} */
const accordionItemStatus = ref({});

const articleComponents = [
  { component: PCPartsDetailsCardArticlesExpected, key: 'expected', title: 'Artículos Previstos' },
  { component: PCPartsDetailsCardArticlesReal, key: 'real', title: 'Artículos Consumidos' },
];

const {
  workPartDetails,
  fetchWorkPartDetails,
} = useRepairParts();

const partStatusFinished = useParamRef('EstadoFinalizacionDefectoParteReparacion');

const isPartFinished = computed(() => !!workPartDetails.value
  && !!workPartDetails.value.Codigo_ParteProduccionEstado
  && workPartDetails.value.Codigo_ParteProduccionEstado === partStatusFinished.value);

function openRealArticlesAccordion() {
  if (Object.hasOwn(accordionItemStatus.value, 'real')) {
    accordionItemStatus.value.real = true;
  }
}

function useWarehousesPopup() {
  /** @type {ShallowRef<InstanceType<typeof PCPartWarehousesPopup> | null>} */
  const warehousesPopup = shallowRef(null);

  function openWarehousesPopup() {
    if (!workPartDetails.value) {
      return;
    }

    warehousesPopup.value?.openPopup(workPartDetails.value);
  }

  /**
   * Handles when an article is added to the warehouse.
   * @param {WorkPartItem} workPartReceived - The received work part.
   */
  function handleWarehouseArticleAdded(workPartReceived) {
    fetchWorkPartDetails(workPartReceived.Id_ParteProduccion);
    openRealArticlesAccordion();
  }

  return {
    warehousesPopup,
    openWarehousesPopup,
    handleWarehouseArticleAdded,
  };
}

function useArticlesPopup() {
  const isAddArticlePopupOpened = ref(false);

  /**
   * Handles when an article is consumed.
   * @param {number} partId - The ID of the work part.
   */
  async function onAddArticleSuccess(partId) {
    fetchWorkPartDetails(partId);
    openRealArticlesAccordion();
  }

  return {
    isAddArticlePopupOpened,
    onAddArticleSuccess,
  };
}

const {
  warehousesPopup,
  openWarehousesPopup,
  handleWarehouseArticleAdded,
} = useWarehousesPopup();

const {
  isAddArticlePopupOpened,
  onAddArticleSuccess,
} = useArticlesPopup();

onMounted(() => {
  for (const item of articleComponents) {
    accordionItemStatus.value[item.key] = true;
  }
});
</script>

<style scoped lang="scss">
.parts-details-card-articles {
  display: flex;
  flex-direction: column;
  padding-left: var(--f7-typography-padding);
  padding-right: var(--f7-typography-padding);

  --f7-list-bg-color: var(--his-light-blue);
  --f7-list-item-title-text-color: var(--his-slate-blue);
  --f7-list-item-border-color: rgb(255 255 255 / 10%);
  --f7-list-chevron-icon-color: var(--his-slate-blue);
}
</style>
